import React, { FC, useRef } from 'react';
import isArray from 'lodash/isArray';
import classNames from 'classnames';
import Utils from '../utils';
import LoadingSpinner from '../components/LoadingSpinner';
import ScrollWrapper from '../common/ScrollWrapper';
import './CopiesList.scss';
import { LoanFormat, mapParsedLoanFormat } from '../../../models/LoanFormat';
import { t } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { mapDiscount } from '../common/translationMappers/discountMapper';
import FormatCurrency from '../utils/formatCurrency';

type Props = {
    readonly copies: ReadonlyArray<any>;
    readonly loanFormat: LoanFormat;
    readonly loading: boolean;
    readonly disabled: boolean;
    readonly onRemove?: (copyId: number) => void;
    readonly netValue?: number;
    readonly currency?: string;
    readonly taxName?: string;
    readonly removeCopyHint?: string;
};

export const CopiesList: FC<Props> = props => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { loanFormat, copies, loading, taxName, netValue, currency } = props;

    if (copies.length === 0 && loading) {
        return (
            <div className="copies-list">
                <LoadingSpinner width={30} />
            </div>
        );
    }

    if (copies.length === 0 || !isArray(copies)) {
        return <div className="copies-list">No copy information available.</div>;
    }

    return (
        <ScrollWrapper ref={wrapperRef} className="copies-list">
            <table>
                <thead>
                    <tr>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td className="a-right strong txt-up">
                            {t('reserveReports.reserveReport.viewReport.yourPrice')}
                        </td>
                        <td>&nbsp;</td>
                    </tr>
                </thead>
                <tbody>
                    {copies.map((copy, idx) => (
                        <CopiesListItem
                            {...props}
                            loanFormat={loanFormat}
                            key={`${copy.id}-${idx}`}
                            copy={copy}
                            idx={idx}
                        />
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3}>
                            <hr className="cold-smoky" style={{ margin: '10px 0', borderTop: '1px solid' }} />
                        </td>
                        <td>&nbsp;</td>
                    </tr>
                    <tr className="sum">
                        <td colSpan={3}>
                            <Trans
                                i18nKey="reserveReports.reserveReport.viewReport.totalExTax"
                                components={[<span className="txt-small" />]}
                                values={{
                                    tax: taxName ?? 'tax'
                                }}
                            />
                            &nbsp;
                            {t('numberFormat.numberWithCurrencySymbol', {
                                val: FormatCurrency.convertIntegerToCurrencyFormat(netValue),
                                currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
                            })}
                        </td>
                        <td>&nbsp;</td>
                    </tr>
                </tfoot>
            </table>
        </ScrollWrapper>
    );
};

type CopiesListItemProps = {
    readonly idx: number;
    readonly loanFormat: LoanFormat;
    readonly copy: any;
    readonly removeCopyHint?: string;
    readonly disabled: boolean;
    readonly onRemove?: (copyId: number) => void;
};

const CopiesListItem: FC<CopiesListItemProps> = ({ loanFormat, copy, removeCopyHint, disabled, onRemove }) => {
    const { t } = useTranslation();
    const title = removeCopyHint || t('removeCopyFromCart');
    const hasDiscount = copy.basePrice && copy.basePrice !== copy.netValue;
    const discountTypeKey = mapDiscount(copy.discountTypeKey);

    return (
        <tr>
            <td className="a-right">1</td>
            <td>
                <div>
                    <strong>
                        {t(`loanFormat.plain.${mapParsedLoanFormat(loanFormat).toLowerCase()}`, {
                            count: 1
                        })}
                    </strong>
                </div>
                <div className="cold-grey">{Utils.truncate(copy.bundle)}</div>
                {copy.bundleTitle && (
                    <div className="cold-grey">
                        <span>{copy.bundleTitle}</span>
                    </div>
                )}
                {copy.savedPercentage > 0 && (
                    <div className="cold-grey">
                        <span>
                            {t(`reserveReports.reserveReport.discountType.${discountTypeKey}`, {
                                percentage: copy.savedPercentage / 100,
                                defaultValue: `-${copy.savedPercentage / 100}% ${copy.discountType}`
                            })}
                        </span>
                    </div>
                )}
            </td>
            <td className="a-right">
                {hasDiscount && (
                    <span className="cold-grey crossed">
                        {t('numberFormat.numberWithCurrencySymbol', {
                            val: FormatCurrency.convertIntegerToCurrencyFormat(copy.basePrice),
                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(copy.currency)
                        })}
                    </span>
                )}
                &nbsp;&nbsp;
                <span className={classNames(hasDiscount && 'cold-dark-red')}>
                    {t('numberFormat.numberWithCurrencySymbol', {
                        val: FormatCurrency.convertIntegerToCurrencyFormat(copy.netValue),
                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(copy.currency)
                    })}
                </span>
            </td>
            <td className="actions">
                {!disabled && !copy.bundleTitle && (
                    <a onClick={() => onRemove?.(copy.id)} title={title}>
                        <i className="material-icons small">delete</i>
                    </a>
                )}
                {!disabled && copy.bundleTitle && (
                    <a title={t('reserveReports.reserveReport.viewReport.notRemoveCopyOfCuratedContent')}>
                        <i className="material-icons small" style={{ color: '#7a7a7a' }}>
                            delete
                        </i>
                    </a>
                )}
            </td>
        </tr>
    );
};
